import {
  computeStatusDefault,
  computeStatusExchangeInline,
  computeStatusExchangeMail,
  computeStatusSpOdToGraph,
  computeStatusWithCode,
} from './utils';

export interface TaskItem {
  type: string;
  status: string;
  hidden: boolean;
  time: string | null;
}

export interface TaskDict {
  [key: string]: TaskItem;
}
export interface AllTaskDict {
  [key: string]: TaskDict;
}

type ComputeStatusFunc = (state: string, statuses: any[]) => string;
type ComputeHiddenFunc = (args: {
  self: TaskItem;
  dict: TaskDict;
  empty: boolean;
  config: { [key: string]: any };
  policyTypes: number[];
  pocProvisionServices: boolean[];
}) => boolean | null;

const CONFIG: {
  [key: string]: {
    defaultStatus: string;
    computeStatus: ComputeStatusFunc;
    defaultHidden: boolean;
    computeHidden: ComputeHiddenFunc | null;
  };
} = {
  DELEGATOR_ACCOUNTS_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  SITE_COLLECTIONS_AND_SUB_SITES_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  ONEDRIVE_PERSONAL_SITES_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  BOX_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.BOX.ENABLED'] &&
        policyTypes.includes(4)
      );
    },
  },
  BOX_SHARE_FOLDER_CREATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.BOX.ENABLED'] &&
        policyTypes.includes(4)
      );
    },
  },
  DROPBOX_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.DROPBOX.ENABLED'] &&
        policyTypes.includes(5)
      );
    },
  },
  DROPBOX_SHARE_FOLDER_VERIFIED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.DROPBOX.ENABLED'] &&
        policyTypes.includes(5)
      );
    },
  },
  GOOGLEDRIVE_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.GOOGLEDRIVE.ENABLED'] &&
        policyTypes.includes(6)
      );
    },
  },
  GOOGLEDRIVE_SHARE_FOLDER_CREATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.GOOGLEDRIVE.ENABLED'] &&
        policyTypes.includes(6)
      );
    },
  },
  SALESFORCE_SANDBOX_SYNC_TARGET_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.SALESFORCE_SANDBOX.ENABLED'] &&
        policyTypes.includes(9)
      );
    },
  },
  SALESFORCE_PRODUCTION_SYNC_TARGET_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['STORAGE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.SALESFORCE_PRODUCTION.ENABLED'] &&
        policyTypes.includes(10)
      );
    },
  },
  RMS_VERIFIED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_EX_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GMAIL_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_TEAMS_INFO_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['TEAMS_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.TEAMS.ENABLED'] &&
        policyTypes.includes(41)
      );
    },
  },
  GRAPH_TEAMS_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_EX_FROM_EWS_TO_GRAPH_STEP: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_SP_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_SITE_COLLECTIONS_AND_SUB_SITES_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_OD_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_ONEDRIVE_PERSONAL_SITES_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_OD_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  UPGRADE_POC_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_TM_CHAT_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_TM_CHAT_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_SP_TO_GRAPH_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_SP_TO_GRAPH_CLEAN_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_OD_TO_GRAPH_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_OD_TO_GRAPH_CLEAN_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusSpOdToGraph,
    defaultHidden: true,
    computeHidden: null,
  },
  GRAPH_MIP_LABELS_INFO_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['TEAMS_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.TEAMS.ENABLED'] &&
        policyTypes.includes(46)
      );
    },
  },
  GRAPH_MIP_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  PROVISION_FOR_EXCHANGE_MAIL_FLOW_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusExchangeMail.bind(null, true),
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['TEAMS_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.TEAMS.ENABLED'] &&
        policyTypes.includes(81)
      );
    },
  },
  DEPROVISION_FOR_EXCHANGE_MAIL_FLOW_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  PROVISION_FOR_EXCHANGE_INLINE_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusExchangeInline,
    defaultHidden: true,
    computeHidden: ({ config, policyTypes }) => {
      return !(
        config['EXCHANGE_INLINE_ENABLED'] &&
        config['SERVER.SERVICE_TYPES.EXCHANGE_INLINE.ENABLED'] &&
        policyTypes.includes(82)
      );
    },
  },
  GRAPH_EX_INLINE_USERS_AND_GROUPS_UPDATED: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  MICROSOFT_IDENTITY_PROTECTION_ACCOUNT_AUTO_PROVISION: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
  PROVISION_FOR_CAS_API_MICROSOFT_IDENTITY_PROTECTION_API: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusDefault,
    defaultHidden: true,
    computeHidden: null,
  },
  MIGRATION_FOR_EXCHANGE_INLINE_STEPS: {
    defaultStatus: 'SUCCESS',
    computeStatus: computeStatusWithCode,
    defaultHidden: true,
    computeHidden: null,
  },
};

export function generateTasks(data: {
  statuses: { [key: string]: any[] };
  policyTypes: { [key: string]: number[] };
}) {
  const allTasks: AllTaskDict = {};

  for (const [guid, statuses] of Object.entries(data.statuses)) {
    if (statuses.length === 0) {
      continue;
    }
    const dict = generateDefault();
    allTasks[guid] = dict;

    let empty = true;
    for (const status of statuses) {
      const cfg = CONFIG[status.type];
      if (!cfg) {
        continue;
      }
      empty = false;
      const item = dict[status.type];
      item.status = cfg.computeStatus(status.value, statuses);
      item.time = status.updatedAt || null;
      item.hidden = false;
    }

    const args = {
      self: null as any,
      dict,
      empty,
      config: {
        STORAGE_ENABLED: true,
        'SERVER.SERVICE_TYPES.BOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.DROPBOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.GOOGLEDRIVE.ENABLED': true,
        'SERVER.SERVICE_TYPES.SALESFORCE_SANDBOX.ENABLED': true,
        'SERVER.SERVICE_TYPES.SALESFORCE_PRODUCTION.ENABLED': true,
        'SERVER.POLICYHOLDER_TYPES.Advanced Threat Protection.ENABLED': true,
        'SERVER.POLICYHOLDER_TYPES.Data Protection.ENABLED': true,
        TEAMS_ENABLED: true,
        'SERVER.SERVICE_TYPES.TEAMS.ENABLED': true,
        'SERVER.SERVICE_TYPES.EXCHANGE_INLINE.ENABLED': true,
        EXCHANGE_INLINE_ENABLED: true,
      } as any,
      policyTypes: data.policyTypes[guid],
      pocProvisionServices: [false, false, false, false],
    };
    for (const item of Object.values(dict)) {
      const cfg = CONFIG[item.type];
      if (item.status.includes('FAILURE')) {
        if (cfg.computeHidden) {
          args.self = item;
          item.hidden = cfg.computeHidden(args) ?? item.hidden;
        }
      }
    }
  }

  return allTasks;
}

function generateDefault() {
  const dict: TaskDict = {};
  for (const [typ, cfg] of Object.entries(CONFIG)) {
    dict[typ] = {
      type: typ,
      hidden: cfg.defaultHidden,
      status: cfg.defaultStatus,
      time: null,
    };
  }
  return dict;
}
